import { isPlatformBrowser } from "@angular/common";
import { isPlatformServer } from "@angular/common";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import { Router } from "@angular/router";
import { REQUEST } from "../express.tokens";
import { NgxPermissionsService } from "ngx-permissions";
import { BehaviorSubject, skip, Subject, takeUntil } from "rxjs";
import { currentUser } from "src/app/Authentication/models/currentUser";
import { AuthenticationService } from "src/app/Authentication/services/authentication.service";
import { RoleService } from "src/app/Authentication/services/role.service";
import { HomePageService } from "src/app/Customer/services/home-page.service";
import { ThemeService } from "src/app/Shared/services/theme.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class InitializationService {
  private homePageService = inject(HomePageService);
  private http = inject(HttpClient);
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private rolesService = inject(RoleService);
  private permissionService = inject(NgxPermissionsService);
  private platformId = inject(PLATFORM_ID);
  private request = inject(REQUEST, { optional: true })!;
  private isBrowser: boolean;

  apiUrl = environment.apiUrl;
  colors: any;
  private onDestroy$ = new Subject<boolean>();
  public currentUserSubject: BehaviorSubject<any>;
  SetHeader: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    const initialUser = this.isBrowser
      ? JSON.parse(localStorage.getItem("currentUser"))
      : null;
    this.currentUserSubject = new BehaviorSubject<currentUser>(initialUser);
  }

  getCurrentUrl(): string {
    if (isPlatformServer(this.platformId)) {
      // If SSR, get the URL from the request object
      return this.request.hostname;
    } else {
      // If browser, get the URL from the location service
      return window.location.host;
    }
  }

  private getFromLocalStorage(key: string): any {
    if (this.isBrowser) {
      return localStorage.getItem(key);
    }
    return null;
  }

  private setToLocalStorage(key: string, value: string): void {
    if (this.isBrowser) {
      localStorage.setItem(key, value);
    }
  }

  private removeFromLocalStorage(key: string): void {
    if (this.isBrowser) {
      localStorage.removeItem(key);
    }
  }

  async InitBaseUrl(): Promise<void> {
    if (this.isBrowser) {
      const currentUserFromStorage = this.getFromLocalStorage("currentUser");
      if (currentUserFromStorage) {
        this.homePageService.configurations.token = JSON.parse(
          currentUserFromStorage
        )?.token;
      }
    }

    let baseUrl: string = environment.production
      ? this.getCurrentUrl()
      : environment.baseUrl;
    let params = new HttpParams().set("baseUrl", baseUrl);
    try {
      const configs: any = await this.http
        .get(`${this.apiUrl}Settings/FetchForBaseUrl`, {
          params: params,
        })
        .pipe(takeUntil(this.onDestroy$))
        .toPromise();

      if (configs?.data) {
        this.homePageService.configurations = configs?.data[0];
        if (typeof configs?.data[0]?.styleSheetFileName === "string") {
          this.themeService?.setTheme(
            JSON.parse(configs?.data[0]?.styleSheetFileName)
          );
        } else {
          console.warn(
            "styleSheetFileName is not a string, cannot parse theme."
          );
        }

        const token =
          this.isBrowser && this.getFromLocalStorage("currentUser")
            ? JSON.parse(this.getFromLocalStorage("currentUser")!)?.token
            : configs?.data[0]?.token;
        this.homePageService.configurations.token = token;

        if (this.isBrowser && this.getFromLocalStorage("currentUser")) {
          await this.getSystemUser(this.homePageService.configurations.token);
        } else {
          this.loadPermissions(this.homePageService.configurations.token);
        }
      } else {
        this.router.navigate(["/error/access-denied"]);
        throw new Error("No configuration data received from API.");
      }
    } catch (err) {
      this.router.navigate(["/error/access-denied"]);
      throw err;
    }
  }

  getSystemUser(token: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.http.get(`${this.apiUrl}Accounts/SystemUser`).subscribe({
        next: (res: any) => {
          if (res.statusCode === 200 && res.data?.length) {
            const model = res.data[0];
            const user = {
              systemUser: model.systemUser,
              firstName: model.firstName,
              lastName: model.lastName,
              siteRoleType: model.siteRoleType,
              vendor: model.vendor,
              cellPhone: model.cellPhone,
              vendorX: model.vendorX,
              isVendorApproved: model.isVendorApproved,
              emailAddress: model.emailAddress,
              siteRoleTypeX: model.siteRoleTypeX,
              token: token,
            };

            this.currentUserSubject.next(user);
            this.homePageService.configurations.token = token;
            this.loadPermissions(token);
            resolve();
          } else {
            reject("Invalid system user response");
          }
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  loadPermissions(accessToken?: string) {
    if (!accessToken) return;

    this.homePageService.configurations.token = accessToken;
    const roles = this.rolesService.getDecodedTokenInfo(accessToken).roles;
    this.homePageService.siteCatalog =
      this.getSiteCatalogFromToken(accessToken);
    this.homePageService!.role = roles[0];

    if (roles?.length) {
      this.permissionService.loadPermissions(roles);
    }
  }

  getSiteCatalogFromToken(accessToken?: string) {
    if (!accessToken) {
      accessToken = this.isBrowser
        ? JSON.parse(this.getFromLocalStorage("currentUser"))?.token
        : null;
    }

    if (!accessToken) return null;

    try {
      const tokenParts = accessToken.split(".");
      const encodedPayload = tokenParts[1];
      const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        "="
      );
      const decodedPayload = atob(padded);
      const payloadObj = JSON.parse(decodedPayload);
      return payloadObj?.SiteCatalog;
    } catch (e) {
      console.error("Error decoding token:", e);
      return null;
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }
}
